import { CalendarIcon, Clock } from "lucide-react";
import { useNavigate } from "react-router";

import { COMPONENT_STATUS_CONFIG } from "@/components/pages_logged_in/program/components/component-utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ComponentStatus, CreateCoachMeeting, ProgramStatus } from "@/graphql";

interface ExistedProgramComponentErrModalProps {
  isOpen: boolean;
  onClose: () => void;
  activePrograms?: CreateCoachMeeting["activePrograms"];
}

function ExistedProgramComponentErrModal({
  isOpen,
  onClose,
  activePrograms,
}: ExistedProgramComponentErrModalProps) {
  const navigate = useNavigate();
  const MAX_PROGRAMS_DISPLAY = 3;
  const limitedPrograms = activePrograms?.slice(0, MAX_PROGRAMS_DISPLAY);

  const handleBookInProgram = () => {
    const firstProgram = limitedPrograms?.[0];
    if (firstProgram) {
      navigate(`/programs/${firstProgram?.id}`);
    }
    // Navigate to the program dashboard
    navigate("/programs");
  };

  const formatDateRange = (startDate?: string, endDate?: string) => {
    if (!startDate || !endDate) return "";

    const start = new Date(startDate);
    const end = new Date(endDate);

    return `${start.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })} - ${end.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })}`;
  };

  // Get status config from the component status configuration
  const getStatusConfig = (status?: ProgramStatus) => {
    if (status === ProgramStatus.Live) {
      return COMPONENT_STATUS_CONFIG[ComponentStatus.Ongoing];
    }

    return {
      color: "#778397",
      backgroundColor: "#F2F4F8",
      text: "Unknown",
    };
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[450px] max-w-md p-6">
        <DialogHeader className="flex space-y-4 text-center">
          <div className="flex flex-row items-center justify-center gap-2">
            <div className="mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-blue-100">
              <CalendarIcon className="h-4 w-4 text-blue-600" />
            </div>

            <DialogTitle className="my-0 flex-1 text-xl font-semibold text-gray-900">
              Great news! You're in a program
            </DialogTitle>
          </div>

          <DialogDescription className="my-0 text-base leading-relaxed text-gray-900">
            You're currently enrolled in a coaching program. To ensure your
            sessions count toward your program progress, please book through
            your program dashboard.
          </DialogDescription>
        </DialogHeader>

        <div className="my-2">
          {limitedPrograms?.map((program) => {
            const statusConfig = getStatusConfig(program?.status);
            return (
              <div
                className="group mb-4 cursor-pointer rounded-lg border border-gray-300 bg-gray-50 p-4 transition-colors hover:bg-gray-100"
                onClick={() => {
                  navigate(`/programs/${program?.id}`);
                }}
                key={program?.id}
              >
                <div key={program?.id} className="mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="my-0 mb-2 font-semibold text-gray-900">
                        {program?.name}
                      </h4>
                      <div className="flex items-center space-x-3 text-sm">
                        <div
                          className="flex items-center space-x-1 rounded-full px-2 py-1"
                          style={{
                            backgroundColor: statusConfig?.backgroundColor,
                            color: statusConfig?.color,
                          }}
                        >
                          <div
                            className="h-2 w-2 rounded-full"
                            style={{
                              backgroundColor: statusConfig?.color,
                            }}
                          ></div>
                          <span className="text-xs font-medium">
                            {statusConfig?.text}
                          </span>
                        </div>
                        {program?.targetLaunchDate &&
                          program?.targetEndDate && (
                            <div className="flex items-center gap-1">
                              <Clock className="h-4 w-4 text-gray-500" />
                              <span className="text-gray-600">
                                {formatDateRange(
                                  program?.targetLaunchDate,
                                  program?.targetEndDate,
                                )}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                    <div>
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex gap-3">
          <Button
            onClick={handleBookInProgram}
            intent="primary"
            className="flex-1"
          >
            Book in Program
          </Button>

          <Button
            onClick={onClose}
            intent="secondary"
            className="border-none bg-gray-200"
          >
            Cancel
          </Button>
        </div>

        <div className="mt-4 text-center">
          <div className="text-sm text-gray-600 transition-colors">
            💡Book a session outside my program
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ExistedProgramComponentErrModal;
